import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { MainView, NoAccessView } from '@repo/ui/Views';
import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';

import MessagesFilter from '../components/MessageFilter';

import { MessagesTable } from '../components/MessagesTable';
import { SelectInputOptions } from '@repo/types/Types';
import { PAGE_SCHEMA } from '@repo/ui/Pagination';
import { z } from 'zod';
import { getNonEmpty } from '@repo/util/nonEmpty';
import { toQueryList } from '@repo/util/toQueryList';
import { combineDateRange } from '@repo/util/combineDateRange';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { Form } from '@repo/ui/Form';
import { useAccount } from '@/hooks/useAccount';

import { coerceValue } from '@repo/util/coerceValueForQuery';
import MessagingTabNav from '../components/MessagingTabNav';

const MessagingPage = () => {
  const user = useUser();

  const account = useAccount();
  const accountId = account.id;

  const canSelectService =
    POLICIES['sms_requests:index'](user) || POLICIES['mms_requests:index'](user);

  const SERVICES: SelectInputOptions = [];
  if (POLICIES['message_requests:index'](user)) {
    SERVICES.push({ label: 'All', value: 'all' });
  }
  if (POLICIES['sms_requests:index'](user)) {
    SERVICES.push({ label: 'SMS', value: 'smses' });
  }
  if (POLICIES['mms_requests:index'](user)) {
    SERVICES.push({ label: 'MMS', value: 'mmses' });
  }

  const STATES: SelectInputOptions = [
    { label: '', value: '' },
    { label: 'Sent', value: 'SENT' },
    { label: 'Error', value: 'ERROR' },
    { label: 'Delivered', value: 'DELIVERED' },
    { label: 'Undelivered', value: 'UNDELIVERED' },
  ] as const;

  const DIRECTIONS: SelectInputOptions = [
    { label: '', value: '' },
    { label: 'Outbound', value: 'OUTBOUND' },
    { label: 'Inbound', value: 'INBOUND' },
  ] as const;

  const today = new Date();
  const oneWeekAgoDate = new Date();
  oneWeekAgoDate.setDate(today.getDate() - 7);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentDate, setCurrentDate] = useState<Date>(today);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [oneWeekAgo, setOneWeekAgo] = useState<Date>(oneWeekAgoDate);

  const MESSAGES_QUERY_SCHEMA = PAGE_SCHEMA.extend({
    message_id: z.string().ulid().or(z.literal('')).default(''),
    service: canSelectService
      ? z.enum(getNonEmpty(SERVICES.map((dir) => dir.value))).default(SERVICES[0].value)
      : z.null(),
    direction: z.enum(getNonEmpty(DIRECTIONS.map((dir) => dir.value))).default(''),
    status: z.enum(getNonEmpty(STATES.map((dir) => dir.value))).default(''),
    host_number: z.string().default(''),
    remote_number: z.string().default(''),
    start: z.coerce.date().optional().default(oneWeekAgo),
    end: z.coerce.date().optional().default(currentDate),
  });

  const filter = useZodQuery({
    schema: MESSAGES_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const [loadedCount, setLoadedCount] = useState<number | null>(null);

  // SERVICE is not query-able in API itself, used to distinguish between SMS and MMS query
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page, start, end, service, message_id, ...queryFields } = filter.queryValues;

  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
        inserted_at: combineDateRange(start ?? oneWeekAgo, end ?? currentDate),
        id: message_id,
      },
      coerceValue,
      ['host_number', 'remote_number']
    ),
  ];

  return POLICIES['sms_requests:index'](user) || POLICIES['mms_requests:index'](user) ? (
    <MainView title="Messaging">
      <MessagingTabNav />

      <div className="mb-12">
        <Form {...filter} variant="horizontal">
          <MessagesFilter
            SERVICES={SERVICES}
            DIRECTIONS={DIRECTIONS}
            STATES={STATES}
            queryList={queryList}
            selectedService={service}
            path={`accounts/${accountId}/${service}`}
            exportFileName={service ? service : 'messages'}
            hasContent={loadedCount === null || loadedCount > 0}
          />
        </Form>
      </div>

      <MessagesTable
        accountId={accountId}
        onLoadedCountChange={setLoadedCount}
        queryList={queryList}
        page={page}
        filter={filter}
        service={service}
      />
    </MainView>
  ) : (
    <NoAccessView />
  );
};

export default MessagingPage;
